import React from 'react';
import {
  ShieldCheck,
  Users,
  Clock,
  Phone,
  Award,
  Building2,
  type LucideIcon,
} from 'lucide-react';

/**
 * TrustBar (Phase 2B) — compact trust strip rendered immediately below the
 * GoogleReviewsCarousel on every page. Each item links to underlying proof
 * (FL DBPR license verifier, BBB profile, warranty page, etc.) so the
 * signals are independently verifiable, not just decorative copy.
 *
 * Visual: horizontal strip, ≤80px tall on desktop, wraps to two rows on
 * mobile. Subtle dividers, monochrome icons, brand orange accent only on
 * hover/focus so the bar reads as authoritative rather than promotional.
 */

interface TrustItem {
  icon: LucideIcon;
  label: string;
  href: string;
  external?: boolean;
  ariaLabel: string;
}

const ITEMS: TrustItem[] = [
  {
    icon: ShieldCheck,
    label: 'Licensed FL EC13011028',
    href: 'https://www.myfloridalicense.com/LicenseDetail.asp?SID=&id=EC13011028',
    external: true,
    ariaLabel:
      'Verify Florida Electrical Contractor License EC13011028 on the Department of Business and Professional Regulation site',
  },
  {
    icon: Users,
    label: 'Family Owned',
    href: '/about',
    ariaLabel: 'About Elevation Electrical, family-owned in Lake Helen FL',
  },
  {
    icon: Clock,
    label: 'Same-Day Service',
    href: '/contact',
    ariaLabel: 'Same-day electrical service across four Central Florida counties',
  },
  {
    icon: Phone,
    label: '24/7 Emergency',
    href: 'tel:4079707211',
    ariaLabel: 'Call our 24/7 emergency electrician line at (407) 970-7211',
  },
  {
    icon: Award,
    label: '5-Year Workmanship Warranty',
    href: '/about',
    ariaLabel: 'Read our 5-year workmanship warranty terms',
  },
  {
    icon: Building2,
    label: 'BBB A+',
    href: 'https://www.bbb.org/us/fl/lake-helen',
    external: true,
    ariaLabel: 'View Better Business Bureau profile for Elevation Electrical',
  },
];

const TrustBar: React.FC = () => {
  return (
    <aside
      aria-label="Credentials and service guarantees"
      className="bg-white border-b border-[#e8eaed]"
    >
      <div className="container-custom">
        <ul
          className="
            flex flex-wrap items-center justify-center
            gap-x-6 gap-y-3 py-3 md:py-4
            text-xs md:text-sm text-navy/80
          "
        >
          {ITEMS.map(({ icon: Icon, label, href, external, ariaLabel }) => {
            const isInternal = !external && !href.startsWith('tel:');
            const Tag = (isInternal ? 'a' : 'a') as 'a';
            return (
              <li
                key={label}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Tag
                  href={href}
                  {...(external && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                  aria-label={ariaLabel}
                  className="
                    inline-flex items-center gap-1.5 group
                    hover:text-orange focus:text-orange
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2 rounded-sm
                    transition-colors
                  "
                >
                  <Icon
                    className="w-4 h-4 text-orange/80 group-hover:text-orange shrink-0"
                    aria-hidden="true"
                  />
                  <span className="font-medium">{label}</span>
                </Tag>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
};

export default TrustBar;
